import {
	BranchAutocompleteDocument,
	type BranchAutocompleteQuery,
	type BranchAutocompleteQueryVariables,
} from "@/generated/graphql";
import type {
	AutocompleteFetchParams,
	AutocompleteFetchResult,
} from "@k3-tech/react-kit";
import { useCallback } from "react";
import { useClient } from "urql";

export const useBranchAutocomplete = () => {
	const urqlClient = useClient();

	const load = useCallback(
		async (
			page: number,
			pageSize: number,
			search: string,
			moreFilter?: AutocompleteFetchParams["moreFilter"],
		) => {
			const { data } = await urqlClient
				.query<BranchAutocompleteQuery, BranchAutocompleteQueryVariables>(
					BranchAutocompleteDocument,
					{
						qop: {
							pagination: {
								page,
								pageSize,
								withCount: true,
							},
							filters: {
								branchName: search,
								...(() => {
									const { branchIDs, ...rest } = moreFilter?.() ?? {};
									// branchIDs may arrive as a CSV string (react-kit
									// fetcherFilter allows only scalars) or already as an
									// array (direct callers); normalize both to an array.
									const normalized =
										typeof branchIDs === "string" && branchIDs.length > 0
											? branchIDs.split(",")
											: Array.isArray(branchIDs) && branchIDs.length > 0
												? branchIDs
												: undefined;
									return {
										...rest,
										...(normalized ? { branchIDs: normalized } : {}),
									};
								})(),
							},
						},
					},
					{ suspense: false, requestPolicy: "network-only" },
				)
				.toPromise();

			const rawItems = (data?.branches?.items ?? []) as NonNullable<
				BranchAutocompleteQuery["branches"]
			>["items"];
			const items = (rawItems || [])
				.filter((it): it is NonNullable<typeof it> => Boolean(it))
				.map((item) => ({
					value: item.branchID,
					label: item.branchName,
					raw: item,
				}));

			return {
				items: items ?? [],
				total: data?.branches?.pagination?.totalRows ?? 0,
				hasMore: data?.branches?.pagination?.hasNext ?? false,
			} as AutocompleteFetchResult;
		},
		[urqlClient],
	);

	return { load };
};
